/**
 * The chrome, plus the one rule that decides whether there is any.
 *
 * `AppFrame` draws the rail and the bar; **this** answers the prior question — may they be
 * drawn at all? MCK-20260812-009 §E: a session whose home workspace is unclaimed is shown no
 * workspace chrome, no workspace name and no settings entry. That is a property of the
 * session, so it is decided here, beside the session query, and not inside a layout component
 * where nobody would think to look for it (retro rule 1 — a rule gets an enforcement point,
 * not a paragraph).
 *
 * `GET /workspace` is fetched once and shared: it is reference data, it changes roughly never,
 * and it already has a query in `features/setup` from B0. Reusing `workspaceQueryOptions`
 * rather than minting a second key for the same resource is the point — two keys for one
 * resource is two answers to one question (CLAUDE.md, data fetching).
 *
 * It is `enabled` on the claim, so an unclaimed session does not spend a request fetching a
 * name it is not allowed to see.
 *
 * WHY THIS LIVES IN `features/projects/` — B1 is its only caller today, and `features/session`
 * cannot hold it without a cycle: `features/setup` already imports `features/session` for the
 * session key, so a chrome component in `session` that reads the workspace query would close
 * the loop. It moves to a shared place the day a second screen family needs it.
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { AppFrame } from '@/components/chrome';
import { ReAcceptanceNotice } from '@/features/legal';
import {
  useActiveWorkspace,
  useActiveWorkspaceId,
  useCompanies,
  useMe,
  useSignOut,
} from '@/features/session';
import { workspaceQueryOptions } from '@/features/setup';
import { useStudioAuthority } from '@/features/studio';

/** The person's initials for the rail avatar — one letter, or two from two words. */
function initialsOf(label: string): string {
  const words = label.split(/[\s@.]+/).filter((word) => word !== '');
  const first = words[0]?.[0] ?? '';
  const second = words.length > 1 ? (words[1]?.[0] ?? '') : '';
  return `${first}${second}`.toUpperCase();
}

export function AppChrome({
  /** Appended after the workspace name. `['Projects']` gives `ACME › Projects`. */
  crumbs,
  tabs,
  phaseBar,
  children,
}: {
  readonly crumbs: readonly string[];
  /** The project tab row, on the screens that are inside a project. */
  readonly tabs?: ReactNode;
  /** The phase bar above it, on the same screens (`KLQ-20260827-002`). */
  readonly phaseBar?: ReactNode;
  readonly children: ReactNode;
}) {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const activeWorkspaceId = useActiveWorkspaceId();
  const active = useActiveWorkspace();
  // §E again, one level up from the suppression below: an unclaimed workspace is not a company
  // and is never offered in the switcher. It also makes "more than one" mean *two firms* rather
  // than *two rows*, which is the question the menu is asking.
  const companies = useCompanies();
  // §E, keyed off the workspace this tab is *acting as* rather than off home
  // (`KLQ-20260819-007`). Identical while a person has one company; for somebody with two it
  // means an unclaimed home no longer suppresses the chrome of the studio that hired them.
  const claimed = active?.claimed ?? me?.home_workspace_claimed ?? false;
  // `select` unwraps the cached pair: the query holds the representation *and* its ETag
  // (`KLQ-20260819-008` §1), and the chrome wants only the company.
  const workspace = useQuery({
    ...workspaceQueryOptions(activeWorkspaceId ?? ''),
    enabled: claimed && activeWorkspaceId !== null,
    select: (held) => held.data,
  });

  // Whether the rail offers the studio-settings entry (`KLQ-20260819-008` §2). It is an
  // authorisation-shaped question, so it is answered here beside the §E rule rather than
  // inside a layout component — and it is `enabled` on the claim for the same reason the
  // workspace read is: an unclaimed session has no firm to be a manager of.
  const { allowed: canAdministerStudio } = useStudioAuthority();

  // Leaving is a session act, so it is owned here rather than inside the rail — `AvatarMenu`
  // takes it as a prop for the same reason it takes the company list.
  const signOut = useSignOut();

  // `RequireSession` renders this only once `me` has resolved; the fallbacks are for the
  // redacted account, whose name and address are tombstoned to `null`.
  const userLabel = me?.display_name ?? me?.email ?? '';
  // The active company's name, from the session's own list — it is already there, it is the
  // one the switcher marks, and reading it from `GET /workspace` would name whichever company
  // that request happened to act as.
  const workspaceName = active?.name ?? workspace.data?.name ?? '';

  if (!claimed) {
    // §E. No rail, no workspace name, no settings entry — the screen renders bare. An invited
    // engineer from another firm has an unclaimed home workspace of their own and has not
    // asked for a company; showing them one would be the product inventing a tenant for them.
    return <main>{children}</main>;
  }

  return (
    <AppFrame
      workspaceInitial={workspaceName.slice(0, 1)}
      canAdministerStudio={canAdministerStudio}
      workspaces={companies}
      activeWorkspaceId={activeWorkspaceId}
      onSignOut={() => {
        signOut.mutate(undefined, {
          // Navigate on settled rather than on success: a sign-out that failed still means
          // the person asked to leave, and the cookies may already be gone.
          onSettled: () => {
            void navigate({ to: '/login', replace: true });
          },
        });
      }}
      signingOut={signOut.isPending}
      userLabel={userLabel}
      userInitials={initialsOf(userLabel)}
      crumbs={[workspaceName, ...crumbs]}
      {...(tabs === undefined ? {} : { tabs })}
      {...(phaseBar === undefined ? {} : { phaseBar })}
      // The re-acceptance prompt (`KLQ-20260814-007` §C), mounted here rather than on a
      // screen because what it answers is a property of the session, which is this file's
      // subject. It is drawn on every signed-in screen and gates none of them: the outstanding
      // list is fetched once and shared, and the notice draws only when something material is
      // owed. The refusal it explains is the server's, on `POST /api/v1/projects` alone.
      notice={<ReAcceptanceNotice />}
    >
      {children}
    </AppFrame>
  );
}
